import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';
import { CommonService } from 'src/app/shared/services/common.service';
import {
	AH_RETURN_BANDS,
	getScoreBand,
	SCORE_BAND_COLORS,
	SCORE_RANGE_COLORS,
	ScoreBandRange,
	UTILIZATION_SCORE_BANDS
} from 'src/app/shared/sct-charts/score-range-colors';

// Battery gauges use the same bands and colors as the % Battery Utilization Scores charts (ST-2681).
// Charger scores are not listed here, so they keep the generic scale below.
const BATTERY_SCORE_BANDS: Record<string, ScoreBandRange[]> = {
	fleet_utilization_score: UTILIZATION_SCORE_BANDS,
	fleet_energy_usage_score: UTILIZATION_SCORE_BANDS,
	'battery-ah-return-score': AH_RETURN_BANDS,
};

@Component({
	selector: 'app-performance-score-widget',
	templateUrl: './performance-score-widget.component.html',
	styleUrls: ['./performance-score-widget.component.css']
})
export class PerformanceScoreWidgetComponent implements OnInit, OnChanges {
	@Input() data: any = {};
	@Input() devices: any = {};
	@Input() performance: boolean = false;
	@Input() toPrint: boolean = false;
	@Input() period: string = '';
	@Input() tooltipType: string = '';
	@Input() showTooltip: boolean = false;

	@Input() scoreConfigs: any[] = [];

	updateScoreConfigs() {
		this.scoreConfigs = Object.keys(this.scoreConfigs).length ? this.scoreConfigs : [
			{
				type: 'fleet_utilization_score',
				titleKey: this.performance || this.toPrint ? "battery_performance.utilization_score" : 'utilization.site_battery_fleet_utilization_score',
				value: 0,
				color: '',
				status: '',
				dashOffset: 0,
				maxValue: 100,
				tooltipKey: this.getTooltip(this.tooltipType, 'utilization'),
			},
			{
				type: 'fleet_energy_usage_score',
				titleKey: this.performance || this.toPrint ? "battery_performance.energy_usage_score" : 'utilization.battery_fleet_energy_usage_score',
				value: 0,
				color: '',
				status: '',
				dashOffset: 0,
				maxValue: 100,
				tooltipKey: this.getTooltip(this.tooltipType, 'energy'),
			},
			{
				type: 'battery-ah-return-score',
				titleKey: this.performance ? "battery_performance.ahr_return_score" : this.toPrint ? "battery_performance.ah_return_score" : "utilization.battlink_ah_return_score_percentage",
				value: 0,
				color: '',
				status: '',
				dashOffset: 0,
				maxValue: 150,
				tooltipKey: this.getTooltip(this.tooltipType, 'ahScore'),
			}
		];
	}

	@Input() userParentFilters: boolean = false;
	@Output() prepareParentFiltersOptions = new EventEmitter<{ batteryVoltagesOptions: any[], batteryCapacitiesOptions: any[], manufacturers: any[] }>();


	color!: string;
	scoreStatus: string = 'Excellent';
	scoreValue: number = 50;

	radius: number = 50;
	circumference: number = 2 * Math.PI * this.radius;
	dashOffset: number = 0;

	periods = [
		{ value: 'last_7_days', label: this.translateService.instant('durations.last_7_days') },
		{ value: 'last_14_days', label: this.translateService.instant('durations.last_14_days') },
		{ value: 'last_30_days', label: this.translateService.instant('durations.last_30_days') },
		{ value: 'last_90_days', label: this.translateService.instant('durations.last_90_days') },
		{ value: 'year_to_date', label: this.translateService.instant('durations.year_to_date') },
		{ value: 'last_year', label: this.translateService.instant('durations.last_year') },
		{ value: 'since_installation', label: this.translateService.instant('durations.all_years') },
	];

	daysFilters = [
		{ value: 'allDays', label: this.translateService.instant('g.all_days') },
		{ value: 'workingDays', label: this.translateService.instant('g.workings_days') },
	];

	@Input() selectedDaysFilter = this.daysFilters[0].value;
	@Input() selectedPeriod = this.periods[0].value;

	batteryVoltagesOptions: any[] = [];
	@Input() selectedBatteryVoltage: string = 'all';
	batteryCapacitiesOptions: any[] = [];
	@Input() selectedBatteryCapacity: string = 'all';

	@Input() selectedBatteryVintage: string[] = ['all'];

	@Input() selectedManufacturer: string = 'all';

	@Input() showNoData: boolean = false;

	noData = true;

	constructor(
		private translateService:TranslateService,
		private commonService: CommonService,
		private siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		this.updateScoreConfigs();
		this.dashOffset =this.circumference - (this.scoreValue / 100) * this.circumference;
		this.formatSelectedData();
	}

	onSelectionChanged(event: { filter: string; value: any; }) {
		switch (event.filter) {
			case 'period':
				this.selectedPeriod = event.value;
				break;
			case 'voltageFilter':
				this.selectedBatteryVoltage = event.value;
				break;
			case 'capacityFilter':
				this.selectedBatteryCapacity = event.value;
				break;
			case 'vintageFilter':
				this.selectedBatteryVintage = event.value;
				break;
			case 'manufacturerFilter':
				this.selectedManufacturer = event.value;
				break;
			default:
				break;
		}

		this.formatSelectedData();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (this.data && Object.keys(this.data).length) {
			this.dashOffset =this.circumference - (this.scoreValue / 100) * this.circumference;
			this.prepareFilters();
			this.formatSelectedData();
		}
		
		if (changes['period']) {
			this.selectedPeriod = this.period;
			this.formatSelectedData();
		}

		if (this.userParentFilters) {
			if (changes?.['selectedPeriod'])
				this.onSelectionChanged({ filter: 'period', value: changes['selectedPeriod'].currentValue });

			if (changes?.['selectedBatteryVoltage'])
				this.onSelectionChanged({ filter: 'voltageFilter', value: changes['selectedBatteryVoltage'].currentValue });

			if (changes?.['selectedBatteryCapacity'])
				this.onSelectionChanged({ filter: 'capacityFilter', value: changes['selectedBatteryCapacity'].currentValue });

			if (changes?.['selectedBatteryVintage'])
				this.onSelectionChanged({ filter: 'vintageFilter', value: changes['selectedBatteryVintage'].currentValue });

			if (changes?.['selectedManufacturer'])
				this.onSelectionChanged({ filter: 'manufacturerFilter', value: changes['selectedManufacturer'].currentValue });
		}
	}

	prepareFilters() {
		const batteriesCapacities: any[] = [];
		const batteriesVoltages: any[] = [];
		let manufacturers: any[] = [];

		for (const device in this.devices) {
			const deviceData = this.devices[device];
			if (deviceData.battery_capacity && !batteriesCapacities.includes(deviceData.battery_capacity))
				batteriesCapacities.push(deviceData.battery_capacity);

			if (deviceData.battery_voltage && !batteriesVoltages.includes(deviceData.battery_voltage))
				batteriesVoltages.push(deviceData.battery_voltage);

			if (deviceData.truck_manufacturer && !manufacturers.includes(deviceData.truck_manufacturer))
				manufacturers.push(deviceData.truck_manufacturer);
		}

		this.batteryVoltagesOptions = this.commonService.prepareOptions(batteriesVoltages);
		this.batteryCapacitiesOptions = this.commonService.prepareOptions(batteriesCapacities);
		manufacturers = this.commonService.prepareOptions(manufacturers);

		this.prepareParentFiltersOptions.emit({ batteryVoltagesOptions: this.batteryVoltagesOptions, batteryCapacitiesOptions: this.batteryCapacitiesOptions, manufacturers });
	}

	formatSelectedData() {
		this.noData = true;

		const selectedData = this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter];
		if (!selectedData) 
			return;

		for (const score of this.scoreConfigs) {
			score.value = this.calculateScore(score.type, score.maxValue, selectedData);
			this.applyScoreStyle(score);
		}
	}

	calculateScore(chartType: string, chartMaxValue: number, selectedData: any): number {
		switch (chartType) {
			case 'fleet_utilization_score': {
				let totalScore = 0;
				let devicesCount = 0;

				for (const device of selectedData.devicesUsageScore || []) {
					if (!this.isIncludedDevice(device.macAddress))
						continue;

					totalScore += device.score;
					devicesCount++;
				}

				if (!devicesCount)
					return 0;

				this.noData = false;
				return Math.round(totalScore / devicesCount);
			}

			case 'fleet_energy_usage_score': {
				let totalScore = 0;
				let devicesCount = 0;

				for (const device of selectedData.devicesUsageScore || []) {
					if (!this.isIncludedDevice(device.macAddress))
						continue;

					totalScore += device.energy_score;
					devicesCount++;
				}

				if (!devicesCount)
					return 0;

				this.noData = false;
				return Math.round(totalScore / devicesCount);
			}

			case 'battery-ah-return-score': {
				let inUseAh = 0;
				let chargeAh = 0;

				for (const device of selectedData.batteryAhScoreList || []) {
					if (!this.isIncludedDevice(device.macAddress))
						continue;

					inUseAh += device.inuseAh;
					chargeAh += device.chargerAh;
				}

				if (!inUseAh || !chargeAh) 
					return 0;

				this.noData = false;

				const score = Math.round((chargeAh / inUseAh) * 100);

				return score > chartMaxValue ? chartMaxValue : score;
			}

			case 'charger_utilization_score': {
				let devicesCount = selectedData.utilization_score.length;
				let totalScore = 0;

				for (const device of selectedData.utilization_score) {
					if (!this.isIncludedDevice(device.mac_address)) {
						devicesCount--;
						continue;
					}

					totalScore += device.value;
				}

				if (devicesCount)
					this.noData = false;

				return devicesCount ? Math.round(totalScore / devicesCount) : 0;
			}
			case 'charger_ah_score': {
				let devicesCount = selectedData.ah_score.length;
				let totalScore = 0;

				for (const device of selectedData.ah_score) {
					if (!this.isIncludedDevice(device.mac_address)) {
						devicesCount--;
						continue;
					}

					totalScore += device.value;
				}

				if (devicesCount)
					this.noData = false;

				return devicesCount ? Math.round(totalScore / devicesCount) : 0;
			}
			case 'charger_ws_score': {
				let devicesCount = selectedData.kwh_score.length;
				let totalScore = 0;

				for (const device of selectedData.kwh_score) {
					if (!this.isIncludedDevice(device.mac_address)) {
						devicesCount--;
						continue;
					}

					totalScore += device.value;
				}

				if (devicesCount)
					this.noData = false;

				return devicesCount ? Math.round(totalScore / devicesCount) : 0;
			}
			default:
				return 0;
		}
	}

	applyScoreStyle(score: any) {
		const value = score.value;
		score.dashOffset = this.circumference - (Math.min(value, 100) / 100) * this.circumference;

		const bands = BATTERY_SCORE_BANDS[score.type];
		if (bands) {
			const band = getScoreBand(bands, value);
			score.color = SCORE_RANGE_COLORS[SCORE_BAND_COLORS[band]];
			score.status = this.translateService.instant(`battery_return_score.${band}`);
			return;
		}

		switch(true) {
			case value >= 80:
				score.color = '#2e7d32';
				score.status = this.translateService.instant('g.excellent');
			break;
			case value >= 60 && value < 80:
				score.color = '#4caf50';
				score.status = this.translateService.instant('g.good');
			break;
			case value >= 40 && value < 60:
				score.color = '#e8e80cff';
				score.status = this.translateService.instant('g.fair');
			break;
			case value >= 20 && value < 40:
				score.color = '#f9a825';
				score.status = this.translateService.instant('g.fair');
			break;
			default:
				score.color = '#d32f2f';
				score.status = this.translateService.instant('g.weak');
			break;
		}
	}

	isIncludedDevice(macAddress: string) {
		if (this.selectedBatteryVoltage !== 'all' && this.devices?.[macAddress]?.battery_voltage !== this.selectedBatteryVoltage)
			return false;

		if (this.selectedBatteryCapacity !== 'all' && this.devices?.[macAddress]?.battery_capacity !== this.selectedBatteryCapacity)
			return false;

		if (!this.siteDashboardService.isVintageWithinFilter(this.devices?.[macAddress], this.selectedBatteryVintage))
			return false;

		if (this.selectedManufacturer !== 'all' && this.devices?.[macAddress]?.truck_manufacturer !== this.selectedManufacturer)
			return false;

		return true;
	}

	getTooltip(type: string, metric: string): string {
		const map: any = {
			device: {
				utilization: 'battery_utilization_scores',
				energy: 'battery_energy_usage_score',
				ahScore: 'battery_ah_score'
			},
			site: {
				utilization: 'site_battery_fleet_utilization_score',
				energy: 'site_battery_fleet_energy_usage_score',
				ahScore: 'site_battery_fleet_ah_score'
			}
		};

		const safeType = map[type] ? type : 'device';

		return map[safeType][metric];
	}

	anyFilterSelected() {
		return this.selectedBatteryVoltage !== 'all' || this.selectedBatteryCapacity !== 'all';
	}
}
